import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getPublishedProductBySlug,
  getPublicProductFiles,
  type PublicProductFile,
} from '@/services/products';
import { products as mockProducts } from '@/services/products';
import { getPublicPlansByProduct } from '@/services/plans';
import {
  getProductReviews,
  checkUserPurchased,
  submitReview,
} from '@/services/reviews';
import { useAuth } from '@/auth/AuthContext';
import type { Product, ProductPlan, Review } from '@/domain/types';

const tabs = ['Description', 'Features', 'Changelog', 'Reviews'] as const;
type Tab = (typeof tabs)[number];

const typeColors: Record<string, string> = {
  plugin: 'bg-brand-50 text-brand-700',
  theme: 'bg-amber-50 text-amber-700',
  template: 'bg-sky-50 text-sky-700',
  addon: 'bg-violet-50 text-violet-700',
  asset: 'bg-rose-50 text-rose-700',
  app: 'bg-teal-50 text-teal-700',
};

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('Description');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [product, setProduct] = useState<Product | null>(null);
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [files, setFiles] = useState<PublicProductFile[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Review form state
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewContent, setReviewContent] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    getPublishedProductBySlug(slug)
      .then(async (prod) => {
        const resolved: Product | null =
          prod ??
          (mockProducts.find((p) => p.slug === slug) as Product | undefined) ??
          null;
        if (!resolved) {
          setNotFound(true);
          return;
        }
        setProduct(resolved);

        const [allPlans, prodFiles, productReviews] = await Promise.all([
          getPublicPlansByProduct(resolved.id).catch(() => []),
          getPublicProductFiles(resolved.id).catch(() => []),
          getProductReviews(resolved.id).catch(() => []),
        ]);

        setPlans(allPlans);
        setFiles(prodFiles);
        setReviews(productReviews);
        setSelectedPlan(allPlans[0]?.id ?? '');

        if (user) {
          checkUserPurchased(resolved.id)
            .then(setHasPurchased)
            .catch(() => {});
        }
      })
      .catch(() => {
        const mock = mockProducts.find((p) => p.slug === slug) as
          | Product
          | undefined;
        if (!mock) {
          setNotFound(true);
          return;
        }
        setProduct(mock);
        getPublicPlansByProduct(mock.id)
          .then((p) => {
            setPlans(p);
            setSelectedPlan(p[0]?.id ?? '');
          })
          .catch(() => {});
      })
      .finally(() => setLoading(false));
  }, [slug, user]);

  const selectedPlanObj = plans.find((p) => p.id === selectedPlan);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;
    setReviewError('');
    setReviewSubmitting(true);
    try {
      await submitReview({
        productId: product.id,
        rating: reviewRating,
        title: reviewTitle,
        content: reviewContent,
      });
      setReviewSubmitted(true);
      setReviewTitle('');
      setReviewContent('');
      setReviewRating(5);
    } catch (err: unknown) {
      setReviewError(
        err instanceof Error ? err.message : 'Failed to submit review.',
      );
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 pt-20 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-surface-400">Loading product…</p>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 pt-20">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-surface-300 mb-4" />
          <h2 className="text-xl font-semibold text-surface-900 mb-2">
            Product not found
          </h2>
          <button
            onClick={() => navigate('/products')}
            className="text-brand-600 font-medium text-sm hover:underline">
            Browse all products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 pt-20 md:pt-24 pb-16">
      <div className="px-4 md:px-6 max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-surface-500 mb-6">
          <Link to="/" className="hover:text-brand-600 transition-colors">
            Home
          </Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <Link
            to="/products"
            className="hover:text-brand-600 transition-colors">
            Products
          </Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-surface-900 font-medium capitalize">
            {product.name}
          </span>
        </div>

        {/* Product Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-10">
          <div className="rounded-2xl overflow-hidden border border-surface-200 shadow-sm bg-white">
            {product.thumbnail ? (
              <img
                src={product.thumbnail}
                alt={product.name}
                className="w-full h-auto object-cover"
              />
            ) : (
              <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-brand-50 to-surface-100">
                <i className="ri-box-3-line text-6xl text-brand-300" />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {(product.typeNames?.length ? product.typeNames : [product.type]).map((t) => (
                <span key={t}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${typeColors[t.toLowerCase()] || 'bg-surface-100 text-surface-600'}`}>
                  {t}
                </span>
              ))}
              <span className="text-xs text-surface-400">
                v{product.version}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                Published
              </span>
              {product.docsHtml && (
                <Link
                  to={`/docs/${product.slug}`}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1 border border-brand-200 bg-brand-50 px-2 py-1 rounded-full">
                  <i className="ri-book-2-line" /> Docs
                </Link>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-surface-900 mb-3">
              {product.name}
            </h1>
            <p className="text-surface-500 text-sm md:text-base leading-relaxed mb-4">
              {product.description || product.shortDescription}
            </p>

            {((product.rating ?? 0) > 0 ||
              (product.sales ?? 0) > 0 ||
              reviews.length > 0) && (
              <div className="flex items-center gap-4 mb-6">
                {reviews.length > 0 && (
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400" />
                    <span className="font-semibold text-surface-900 text-sm">
                      {(
                        reviews.reduce((s, r) => s + r.rating, 0) /
                        reviews.length
                      ).toFixed(1)}
                    </span>
                    <span className="text-sm text-surface-400">
                      ({reviews.length} reviews)
                    </span>
                  </div>
                )}
                {(product.sales ?? 0) > 0 && (
                  <span className="text-sm text-surface-400">
                    {(product.sales ?? 0).toLocaleString()} sales
                  </span>
                )}
              </div>
            )}

            {/* Plan Selector */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-surface-900 mb-3">
                Select a plan
              </h3>
              {plans.length === 0 ? (
                <p className="text-sm text-surface-400 italic">
                  No plans available yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {plans.map((plan) => {
                    const sitesLabel =
                      (plan.siteLimit ?? 1) >= 999
                        ? 'Unlimited sites'
                        : `${plan.siteLimit} site${plan.siteLimit !== 1 ? 's' : ''}`;
                    return (
                      <label
                        key={plan.id}
                        className={`flex items-center gap-3 p-3 md:p-4 rounded-xl border cursor-pointer transition-all ${selectedPlan === plan.id ? 'border-brand-500 bg-brand-50/50 shadow-sm' : 'border-surface-200 hover:border-brand-300 bg-white'}`}>
                        <input
                          type="radio"
                          name="plan"
                          value={plan.id}
                          checked={selectedPlan === plan.id}
                          onChange={() => setSelectedPlan(plan.id)}
                          className="w-4 h-4 accent-brand-600 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-surface-900 text-sm">
                              {plan.name}
                            </span>
                            <span className="font-bold text-surface-900">
                              ${plan.price}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            <span className="text-xs text-surface-500">
                              {sitesLabel}
                            </span>
                            {(plan.features ?? []).slice(0, 2).map((f) => (
                              <span
                                key={f}
                                className="text-xs text-surface-400">
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                disabled={!selectedPlan}
                onClick={() =>
                  navigate(
                    `/checkout?productId=${product.id}&planId=${selectedPlan}`,
                  )
                }
                className="flex-1 px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors whitespace-nowrap">
                {selectedPlanObj
                  ? `Get ${selectedPlanObj.name} — $${selectedPlanObj.price}`
                  : 'Select a Plan Above'}
              </button>
              <button className="px-6 py-3 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-1.5 justify-center">
                <i className="ri-heart-line" /> Save
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="flex items-center border-b border-surface-200 overflow-x-auto px-4 md:px-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === tab ? 'border-brand-600 text-brand-600' : 'border-transparent text-surface-500 hover:text-surface-700'}`}>
                {tab}
                {tab === 'Changelog' &&
                  (product.changelogHtml || files.length > 0) && (
                    <span className="text-[10px] bg-surface-100 text-surface-400 font-semibold px-1.5 py-0.5 rounded-full">
                      {files.length || '✓'}
                    </span>
                  )}
                {tab === 'Reviews' && reviews.length > 0 && (
                  <span className="text-[10px] bg-surface-100 text-surface-400 font-semibold px-1.5 py-0.5 rounded-full">
                    {reviews.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-4 md:p-6">
            {/* ── Description ── */}
            {activeTab === 'Description' && (
              <div className="max-w-3xl">
                <p className="text-sm md:text-base text-surface-600 leading-relaxed mb-6">
                  {product.description || product.shortDescription}
                </p>
                {product.features.length > 0 && (
                  <>
                    <h3 className="font-semibold text-surface-900 mb-3">
                      What is included
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                      {product.features.map((f) => (
                        <li
                          key={f}
                          className="flex items-start gap-2 text-sm text-surface-600">
                          <i className="ri-check-line text-brand-500 mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="flex flex-wrap gap-4 text-xs text-surface-500">
                  <span>
                    <i className="ri-wordpress-line mr-1" />
                    Requires WP 6.2+
                  </span>
                  <span>
                    <i className="ri-php-line mr-1" />
                    PHP 7.4+
                  </span>
                  <span>
                    <i className="ri-download-line mr-1" />
                    Instant download
                  </span>
                </div>
              </div>
            )}

            {/* ── Features ── HTML iframe or fallback cards */}
            {activeTab === 'Features' &&
              (product.featuresHtml ? (
                <iframe
                  srcDoc={product.featuresHtml}
                  className="w-full rounded-lg border-0"
                  style={{ minHeight: 500, height: '60vh' }}
                  title="Product Features"
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
                  {product.features.length === 0 ? (
                    <p className="text-sm text-surface-500 col-span-2">
                      No features listed.
                    </p>
                  ) : (
                    product.features.map((f) => (
                      <div
                        key={f}
                        className="p-4 rounded-lg bg-surface-50 border border-surface-100">
                        <i className="ri-check-double-line text-brand-500 mb-2 block" />
                        <h4 className="font-medium text-surface-900 text-sm mb-1">
                          {f}
                        </h4>
                        <p className="text-xs text-surface-500">
                          Fully supported and tested with latest WordPress
                          releases.
                        </p>
                      </div>
                    ))
                  )}
                </div>
              ))}

            {/* ── Changelog ── HTML iframe or fallback file list */}
            {activeTab === 'Changelog' &&
              (product.changelogHtml ? (
                <iframe
                  srcDoc={product.changelogHtml}
                  className="w-full rounded-lg border-0"
                  style={{ minHeight: 500, height: '60vh' }}
                  title="Changelog"
                />
              ) : (
                <div className="space-y-4 max-w-3xl">
                  {files.length === 0 ? (
                    <p className="text-sm text-surface-500">
                      No releases published yet.
                    </p>
                  ) : (
                    files.map((file) => (
                      <div
                        key={file.id}
                        className="flex gap-4 p-4 rounded-lg bg-surface-50 border border-surface-100">
                        <div className="shrink-0">
                          <span className="inline-block px-2 py-1 rounded-md bg-brand-50 text-brand-700 text-xs font-semibold">
                            v{file.version}
                          </span>
                          <p className="text-xs text-surface-400 mt-1 whitespace-nowrap">
                            {new Date(file.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              },
                            )}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-surface-700 leading-relaxed whitespace-pre-line">
                            {file.changelog || (
                              <span className="italic text-surface-400">
                                No notes for this release.
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-surface-400">
                            {file.requiresWp && (
                              <span>WP {file.requiresWp}+</span>
                            )}
                            {file.requiresPhp && (
                              <span>PHP {file.requiresPhp}+</span>
                            )}
                            {file.testedWp && (
                              <span>Tested up to {file.testedWp}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ))}

            {/* ── Reviews ── */}
            {activeTab === 'Reviews' && (
              <div className="max-w-3xl space-y-6">
                {/* ── Reviews list — always public ── */}
                {reviews.length === 0 ? (
                  <div className="text-center py-10 border border-surface-100 rounded-xl bg-surface-50">
                    <i className="ri-star-line text-4xl text-surface-200 block mb-2" />
                    <p className="text-sm font-medium text-surface-600">
                      No reviews yet
                    </p>
                    <p className="text-xs text-surface-400 mt-1">
                      Be the first to share your experience.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Average rating header */}
                    <div className="flex items-center gap-3 pb-4 border-b border-surface-100">
                      <span className="text-3xl font-bold text-surface-900">
                        {(
                          reviews.reduce((s, r) => s + r.rating, 0) /
                          reviews.length
                        ).toFixed(1)}
                      </span>
                      <div>
                        <div className="flex items-center gap-0.5 mb-0.5">
                          {Array.from({ length: 5 }).map((_, i) => {
                            const avg =
                              reviews.reduce((s, r) => s + r.rating, 0) /
                              reviews.length;
                            return (
                              <i
                                key={i}
                                className={`ri-star-fill text-sm ${i < Math.round(avg) ? 'text-amber-400' : 'text-surface-200'}`}
                              />
                            );
                          })}
                        </div>
                        <p className="text-xs text-surface-400">
                          {reviews.length} review
                          {reviews.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {reviews.map((r) => (
                        <div
                          key={r.id}
                          className="p-4 rounded-xl border border-surface-200 bg-white">
                          <div className="flex items-center gap-3 mb-3">
                            {r.reviewerAvatar ? (
                              <img
                                src={r.reviewerAvatar}
                                alt={r.reviewerName}
                                className="w-9 h-9 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-sm shrink-0">
                                {r.reviewerName
                                  ? r.reviewerName[0].toUpperCase()
                                  : '?'}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-surface-900 truncate">
                                {r.reviewerName || 'Anonymous'}
                              </p>
                              {r.reviewerRole && (
                                <p className="text-xs text-surface-400 truncate">
                                  {r.reviewerRole}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 mb-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i
                                key={i}
                                className={`ri-star-fill text-xs ${i < r.rating ? 'text-amber-400' : 'text-surface-200'}`}
                              />
                            ))}
                          </div>
                          {r.title && (
                            <p className="text-sm font-semibold text-surface-900 mb-1">
                              {r.title}
                            </p>
                          )}
                          <p className="text-sm text-surface-600 leading-relaxed">
                            {r.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* ── Write a review — purchasers only ── */}
                <div className="border-t border-surface-100 pt-6">
                  {reviewSubmitted ? (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
                      <i className="ri-check-circle-line text-xl shrink-0" />
                      <div>
                        <p className="font-semibold">Review submitted!</p>
                        <p className="text-xs text-emerald-600 mt-0.5">
                          It will appear once approved by our team.
                        </p>
                      </div>
                    </div>
                  ) : user && hasPurchased ? (
                    <div className="bg-surface-50 rounded-xl border border-surface-200 p-5">
                      <h3 className="font-semibold text-surface-900 mb-4 text-sm flex items-center gap-2">
                        <i className="ri-star-line text-amber-400" /> Write a
                        Review
                      </h3>
                      <form onSubmit={handleSubmitReview} className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
                            Your Rating
                          </label>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setReviewRating(star)}
                                className="text-2xl leading-none transition-transform hover:scale-110">
                                <i
                                  className={`ri-star-fill ${star <= reviewRating ? 'text-amber-400' : 'text-surface-200'}`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                            Title
                          </label>
                          <input
                            type="text"
                            value={reviewTitle}
                            onChange={(e) => setReviewTitle(e.target.value)}
                            required
                            placeholder="Summarise your experience"
                            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                            Review
                          </label>
                          <textarea
                            value={reviewContent}
                            onChange={(e) => setReviewContent(e.target.value)}
                            required
                            rows={4}
                            placeholder="Share your experience with this product…"
                            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none"
                          />
                        </div>
                        {reviewError && (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <i className="ri-error-warning-line" />{' '}
                            {reviewError}
                          </p>
                        )}
                        <button
                          type="submit"
                          disabled={reviewSubmitting}
                          className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2">
                          {reviewSubmitting && (
                            <i className="ri-loader-4-line animate-spin" />
                          )}
                          {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
                        </button>
                      </form>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-surface-50 border border-surface-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-surface-200 flex items-center justify-center shrink-0">
                          <i className="ri-lock-line text-surface-500 text-sm" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-surface-700">
                            Leave a review
                          </p>
                          <p className="text-xs text-surface-400 mt-0.5">
                            {!user
                              ? 'Sign in and purchase this product to write a review.'
                              : 'Purchase this product to write a review.'}
                          </p>
                        </div>
                      </div>
                      {!user && (
                        <Link
                          to="/login"
                          className="shrink-0 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors">
                          Sign In
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
